/**
 * Moroccan words for Darija, applied to the whole Darija bundle once, when the messages are built.
 *
 * Darija starts from the Modern Standard Arabic text and overrides what was written by hand, so every
 * string nobody rewrote still carries the standard word. A Moroccan says "كليان", not "الزبون"; "ليفرور",
 * not "الموزّع"; "كارديان", not "الوكيل"; "كولي", not "الطرد"; "كود", not "الرمز". The hand-written Darija
 * already uses these (see the courier and agent screens), so this keeps the inherited text on the same
 * words instead of editing hundreds of strings one by one. A new term goes in `RULES`, longest word first
 * so a plural is not caught by its singular.
 *
 * The Arabic (`ar`) bundle is untouched: for Modern Standard Arabic the standard words are right.
 */
const RULES: ReadonlyArray<readonly [RegExp, string]> = [
  // The person sending: client.
  [/الزبناء|العملاء/g, 'الكليان'],
  [/زبناء|عملاء/g, 'كليان'],
  [/الزبون|العميل/g, 'الكليان'],
  [/زبون|عميل/g, 'كليان'],
  // The courier: livreur.
  [/الموزّعين|الموزعين|السائقين/g, 'الليفرورات'],
  [/موزّعين|موزعين/g, 'ليفرورات'],
  [/الموزّع|الموزع|السائق/g, 'الليفرور'],
  [/موزّع|موزع/g, 'ليفرور'],
  // The relay agent: gardien.
  [/الوكلاء/g, 'الكارديانات'],
  [/وكلاء/g, 'كارديانات'],
  [/الوكيل/g, 'الكارديان'],
  [/(?<!ت)وكيل/g, 'كارديان'],
  // The parcel: colis.
  [/الطرود|الشحنات/g, 'الكوليات'],
  [/طرود|شحنات/g, 'كوليات'],
  [/الطرد|الشحنة/g, 'الكولي'],
  [/طرد|شحنة/g, 'كولي'],
  // The code a person reads out.
  [/الرموز/g, 'الكودات'],
  [/رموز/g, 'كودات'],
  [/الرمز(?![يةا])/g, 'الكود'],
  [/رمز(?![يةا])/g, 'كود']
]

export function darijaText(text: string): string {
  return RULES.reduce((out, [pattern, word]) => out.replace(pattern, word), text)
}

/** The same bundle with every string in Moroccan words; numbers, arrays and keys are left as they are. */
export function toDarija<T>(value: T): T {
  if (typeof value === 'string') return darijaText(value) as T
  if (Array.isArray(value)) return value.map(toDarija) as T
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, toDarija(item)])) as T
  }

  return value
}
